import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ComplexPromotion3P } from '@mango-engineering/maknet-client-sdk/lib/shop/product/contract/product.contract';
import { CollectionType } from '@mango-engineering/maknet-client-sdk/lib/shop/product/enum/collectionType.enum';
import { ProductModel } from '@mango-engineering/maknet-client-sdk/lib/shop/product/model/product.model';
import { getProPointPromotions } from '@mango-engineering/maknet-client-sdk/lib/shop/product/util/productUtils';
import { COUNTRY } from '@makro-shopping/product-card';
import { getCurrencySymbol, PROMOTION_SUGGESTION_TARGET_UNIT } from '@makro-shopping/use-product-card-logic';
import { config } from '../config/env';
import { useDynamicData, useStaticData } from '../contexts/DataProvider.context';
import { Collection, Product, ProductSearchResult, PromoAdvice } from '../types/product.types';
import { getFeatureMinVersion, isFeatureEnabled } from '../utils/common.util';
import { FALLBACK_APP_VERSION, RM3_COMPLEX_PROMOTION } from '../utils/constants';
import { getCurrencyByCountry } from '../utils/country.util';
import { formatPriceWithoutDecimal } from '../utils/currency';
import { isFlagEnabledForCountry } from '../utils/helper';
import i18n from '../utils/i18n';
import { getProductIdWithoutPrefix } from '../utils/product.util';
import {
  buildStrapiCDNURL,
  ComplexPromotionIconMap,
  ComplexPromotionType,
  LOYALTY_AND_CAMPAIGN_CDN_FILE_NAMES,
  PROMOTION_REWARD,
  PROPOINT_COLLECTION,
} from '../utils/proPointsPromotion.util';

export const RIBBONS_TITLE = {
  POINT: 'point',
  LOYALTY: 'loyalty',
};

function stripPromotionArrowToken(text: string): string {
  return text.replace(/<arrow>/gi, '').trim();
}

function ribbonTextFromCollectionPromotion(collection: Collection | undefined, locale: string): string | undefined {
  const promo = collection?.promotion;
  if (!collection || !promo) {
    return undefined;
  }
  if (promo.t1?.trim()) {
    return stripPromotionArrowToken(promo.t1);
  }
  const titleCandidates =
    locale === 'en'
      ? [collection.titleAlt, collection.title, promo.title]
      : [collection.title, ...(locale === 'th' ? [promo.titleTh] : []), promo.title];
  const localizedTitle = titleCandidates.find((title) => title?.trim());
  if (localizedTitle?.trim()) {
    return stripPromotionArrowToken(localizedTitle);
  }
  return undefined;
}

function firstCollectionRibbonFallback(
  collections: Product['collections'] | ProductSearchResult['collections'],
  locale: string,
): string | undefined {
  if (!collections?.length) {
    return undefined;
  }
  for (const c of collections) {
    if (typeof c === 'string') {
      continue;
    }
    const text = ribbonTextFromCollectionPromotion(c, locale);
    if (text) {
      return text;
    }
  }
  return undefined;
}

export default function usePromotionRibbon(product: Product | ProductSearchResult) {
  const { complexPromotion } = useDynamicData();
  const { countryCode, isNative, appVersion } = useStaticData();
  const isComplexPriority = config.LOYALTY_COMPLEX_PROMOTION_PRIORITY ?? true;
  const isPromoTagV2Config = config.LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED;
  const enableHorizonProductCard = config.ENABLE_HORIZON_PRODUCT_CARD;

  const isHorizonProductCardEnabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, enableHorizonProductCard) && isNative);
  }, [countryCode, enableHorizonProductCard, isNative]);

  const isHorizonUiUxEnabled = useMemo(() => {
    const horizonUiUxMinVersion = getFeatureMinVersion(countryCode, config.HORIZON_UI_UX_MIN_VERSION);
    return isHorizonProductCardEnabled && isFeatureEnabled(horizonUiUxMinVersion, appVersion || FALLBACK_APP_VERSION);
  }, [appVersion, countryCode, isHorizonProductCardEnabled]);

  const isLoyaltyCampaignPromoTagV2Enabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, isPromoTagV2Config) && isNative && isHorizonUiUxEnabled);
  }, [countryCode, isPromoTagV2Config, isNative, isHorizonUiUxEnabled]);

  const { t } = useTranslation();
  const locale = i18n.language;
  const isLocaleLang = locale !== 'en';
  const currencyCode = getCurrencyByCountry(countryCode as COUNTRY);
  const currencySymbol = getCurrencySymbol(currencyCode);
  const { promotionSuggestion, proPointPromotionsApplicable, totalPromotions } = useMemo(() => {
    return getProPointPromotions(product as unknown as ProductModel, isLocaleLang);
  }, [isLocaleLang, product]);

  let title = '';
  let type = PROPOINT_COLLECTION;
  let iconUrl = buildStrapiCDNURL(LOYALTY_AND_CAMPAIGN_CDN_FILE_NAMES.proPointIcon);
  let subTypeLocalized: string | undefined;

  const productIdProcessed = product.id?.replace('gid://shopify/Product/', '');
  const promotion = complexPromotion[product.sku] ||
    (product?.makroId ? complexPromotion[product.makroId] : undefined) ||
    (product.id ? complexPromotion[product.id] : undefined) ||
    (productIdProcessed ? complexPromotion[productIdProcessed] : undefined) || {
      promoAdvice: [],
      loading: false,
    };

    const hasProductRM3Promotion =
    config.LOYALTY_RM3_PROMOTION_FROM_PRODUCT_API &&
    product.collections?.some((p) => (p.type as string) === CollectionType.RM3_COMPLEX_PROMOTION);

  const isComplexPromotionEnabled = config.LOYALTY_COMPLEX_PROMOTION_ENABLED;
  let hasComplexPromotion = false;
  const hasProPointPromotion = product.collections?.some((p) => {
    return (p.type as string) === CollectionType.PROPOINT_PROMOTION;
  });

  const flatComplexPromotion3P: ComplexPromotion3P[] = [];
  for (const variant of product.variants || []) {
    flatComplexPromotion3P.push(...(variant?.complexPromotion3P || []));
  }

  if (promotion && isComplexPromotionEnabled) {
    hasComplexPromotion = promotion.promoAdvice.length > 0 || flatComplexPromotion3P.length > 0;
  }

  const isPlpProPointPromotionEnabled = proPointPromotionsApplicable && config.PLP_PROPOINT_PROMOTION_ENABLED;
  const isAsPlpProPointPromotionEnabled = proPointPromotionsApplicable && config.AS_PLP_PROPOINT_PROMOTION_ENABLED;

  const isAsComplexPromotionEnabled = hasComplexPromotion && config.AS_PLP_PROPOINT_PROMOTION_ENABLED;

  const fetchPromotionTitle = (complexPromotion: PromoAdvice) => {
    if (isLocaleLang) {
      return complexPromotion?.titleAlt || complexPromotion.title;
    }
    return complexPromotion.title;
  };

  // Get collection handle and ID for navigation - will be updated if complex is prioritized
  const proPointCollection = product.collections?.find((c) => (c.type as string) === CollectionType.PROPOINT_PROMOTION);
  let collectionId = proPointCollection?.id;
  let collectionHandle = proPointCollection?.handle;
  let collection = proPointCollection?.promotion?.collection;
  const productId = getProductIdWithoutPrefix(product.id);
  let prioritizedPromotion: PromoAdvice | ComplexPromotion3P | null = null;

  const getComplexPromotionTitle = () => {
    // Priority: Regular complex promotions take priority over 3P promotions
    // This matches React Native's useMergeComplexPromotion behavior
    const hasRegularComplexPromotion = promotion.promoAdvice.length > 0;
    const has3PComplexPromotion = flatComplexPromotion3P.length > 0;

    // Prioritize regular complex promotion over 3P
    if (hasRegularComplexPromotion) {
      const [complexPromotion] = promotion.promoAdvice;
      prioritizedPromotion = complexPromotion;

      // Find the collection for this complex promotion from product.collections
      const complexPromotionCollection = product.collections?.find((c) => {
        return (c.type as string) === RM3_COMPLEX_PROMOTION;
      });

      // Update collection info to point to complex promotion collection
      if (complexPromotionCollection) {
        collectionId = complexPromotionCollection.id;
        collectionHandle = complexPromotionCollection.handle;
        collection = complexPromotionCollection.promotion?.collection;
      }

      const promotionType = complexPromotion.subType;
      type = complexPromotion.type;
      subTypeLocalized = complexPromotion.subTypeLocalized;

      // Native Horizon / merge: prefer collection `promotion.t1` over RM3 API title when present.
      const ribbonFromCollection = ribbonTextFromCollectionPromotion(complexPromotionCollection, locale);
      if (ribbonFromCollection) {
        title = ribbonFromCollection;
      } else if (promotionType === ComplexPromotionType.BOGO && complexPromotion.title) {
        title = stripPromotionArrowToken(complexPromotion.title);
      } else if (promotionType === ComplexPromotionType.BOGO) {
        title = t('complexPromotions.bogoPromotion');
      } else {
        title = stripPromotionArrowToken(fetchPromotionTitle(complexPromotion));
      }

      // Update icon for new design but keep the original title text
      if (isLoyaltyCampaignPromoTagV2Enabled) {
        iconUrl = buildStrapiCDNURL(ComplexPromotionIconMap[complexPromotion.subType]);
      }
      return; // Exit early - don't process 3P since regular complex takes priority
    }

    // Only use 3P if there's no regular complex promotion
    if (has3PComplexPromotion) {
      const [promo] = flatComplexPromotion3P;
      prioritizedPromotion = promo;
      const { rewardValue, rewardType } = promo;

      title = stripPromotionArrowToken(promo.title);
      type = ComplexPromotionType.SELLER_PROMO;

      if (isLoyaltyCampaignPromoTagV2Enabled && rewardValue && rewardValue > 0) {
        if (rewardType === PROMOTION_REWARD.PERCENTAGE_OFF_3P) {
          title = `${t('labels.promotion.discountOff', { percentage: rewardValue })}`;
        } else if (rewardType === PROMOTION_REWARD.BATH_OFF_3P)
          title = `${t('labels.promotion.amountOff', { rewardValue })}`;
      }

      // Update icon for new design but keep the original title text
      if (isLoyaltyCampaignPromoTagV2Enabled) {
        if ((product as Product).brandDetails?.logoUrl) {
          iconUrl = (product as Product)?.brandDetails?.logoUrl || '';
        }
      } else {
        if ((product as Product).brandDetails?.logoUrl) {
          iconUrl = (product as Product)?.brandDetails?.logoUrl || '';
        }
      }
    }
  };

  if (proPointPromotionsApplicable) {
    const suggestion = promotionSuggestion as {
      targetType: string;
      rewardType: string;
      targetValue: number;
      rewardValue: number;
      t1?: string;
      t2?: string;
      t3?: string;
      collection?: {
        t1?: string;
      };
    };
    const proPointRibbon = ribbonTextFromCollectionPromotion(proPointCollection, locale);
    if (suggestion.t1) {
      title = stripPromotionArrowToken(suggestion.t1);
    } else if (proPointRibbon) {
      title = proPointRibbon;
    } else if (suggestion.collection?.t1) {
      title = stripPromotionArrowToken(suggestion.collection.t1);
    } else {
      const promotionTextKey =
        suggestion.targetType === PROMOTION_SUGGESTION_TARGET_UNIT
          ? 'labels.proPointSuggestionUnits'
          : 'labels.proPointSuggestion';

      title = t(promotionTextKey, {
        targetValue: formatPriceWithoutDecimal(promotionSuggestion.targetValue),
        rewardValue: promotionSuggestion.rewardValue,
        currencySymbol,
      });
    }
  } else {
    //check if complex promotion exist
    if (hasComplexPromotion) {
      getComplexPromotionTitle();
    }
  }

  if (isComplexPriority && hasComplexPromotion) {
    getComplexPromotionTitle();
  }

  if (!title?.trim()) {
    const fallbackRibbon = firstCollectionRibbonFallback(product.collections, locale);
    if (fallbackRibbon) {
      title = fallbackRibbon;
    }
  }

  const promotionCountProcessed = useMemo(() => {
    const complexPromotion3PCount = flatComplexPromotion3P.length;
    if ((product as Product).campaignPromotionsCount !== undefined) {
      // Product API campaign count does not include 3P seller promos from variants.
      return (product as Product).campaignPromotionsCount! + complexPromotion3PCount;
    }
    const productRm3Count = hasProductRM3Promotion
      ? (product.collections?.filter((c) => (c.type as string) === CollectionType.RM3_COMPLEX_PROMOTION).length ?? 0)
      : 0;
    return (
      totalPromotions +
      (hasProductRM3Promotion ? 0 : promotion.promoAdvice.length) +
      complexPromotion3PCount +
      productRm3Count
    );
  }, [product, hasProductRM3Promotion, totalPromotions, promotion.promoAdvice.length, flatComplexPromotion3P.length]);

  let displayRibbon = !promotion.loading;
  const promotionCollectionCount =
    product.collections?.filter((c) => {
      return (c.type as string) === CollectionType.PROMOTION;
    }).length || 0;
  const isPointTitle = product?.collections?.some((p) => {
    if ((p.type as string) !== CollectionType.PROMOTION) {
      return false;
    }

    const enTitle = isLocaleLang ? p.titleAlt : p.title;

    return enTitle?.toLowerCase().includes(RIBBONS_TITLE.POINT);
  });
  if (promotionCollectionCount) {
    //has manual promotion check condition
    //case 1 has automate and complex
    if (hasProPointPromotion && hasComplexPromotion) {
      displayRibbon = false;
    }

    //case 2 has suggestion don't have complex
    if (hasProPointPromotion && !hasComplexPromotion) {
      //check if it has point title if yes we need to hide it

      if (isPointTitle) {
        displayRibbon = false;
      } else {
        displayRibbon = true;
      }
    }

    //case 3 has complex don't have suggestion
    if (!hasProPointPromotion && hasComplexPromotion) {
      //show if contain point
      if (isPointTitle) {
        displayRibbon = true;
      } else {
        displayRibbon = false;
      }
    }

    //case 4 has don't have any
    if (!hasProPointPromotion && !hasComplexPromotion) {
      displayRibbon = true;
    }
  }

  // Determine if we're showing complex promotion based on priority
  const isShowingComplexPromotion = isComplexPriority && hasComplexPromotion;

  return {
    title,
    totalPromotions: promotionCountProcessed,
    complexPromotionLoading: promotion?.loading,
    isPlpProPointPromotionEnabled,
    isAsPlpProPointPromotionEnabled,
    type,
    hasComplexPromotion,
    isAsComplexPromotionEnabled,
    displayRibbon,
    iconUrl,
    subTypeLocalized,
    collectionId,
    collectionHandle,
    productId,
    collection,
    prioritizedPromotion,
    isComplexPromotion: isShowingComplexPromotion,
  };
}
